// Copyright (c) Docugami, Inc. All rights reserved.

import styled from "styled-components"
import { primaryTheme, subheadGrey } from 'components/shell/presentation/AppStyles';
import { userFontFamily } from 'components/common/Fonts';
import { WorkPath } from "networking/WorkPathApi";
import { WorkPathHeaderEditableName, WorkPathHeaderDocSetName, HeaderInfoSection, WorkPathCommandBar, HeaderInfoSectionCentered } from "./WorkPathHeaderComponents";
import { ReportReviewButton } from "./ReportReviewButton";
import { AutomationId, getAutomationIdForInfoButton, getAutomationIdForReportPageAnchor } from "utils/Automation";
import { useAppSelector } from "hooks/redux";
import { selectIsReportPublished, selectIsAnyReportDocumentPublishing, selectReportContext, selectReportViewMode } from "store/reports/selectors";
import { ReportViewContext } from "networking/reportsApi";
import { ReportViewModeSwitcher } from "./ReportViewModeSwitcher";
import { useReportViewModes } from "hooks/ReportViewModes";

const Container = styled.div`
    display: flex;
    background: ${subheadGrey};
    min-height: 45px;
    align-items: center;
    font-family: ${userFontFamily};
    gap: 15px;
    z-index: 1;
    padding-left: 10px;
`;

const ActionsContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-left: auto;
    box-sizing: border-box;
    gap: 15px;
    >:first-child {
        margin-right: 10px;
    }
`

const StyledReportViewModeSwitcher = styled(ReportViewModeSwitcher)`
    background: ${primaryTheme.palette.white};
    border-radius: 3px;
`

const StyledWorkPathCommandBar = styled(WorkPathCommandBar)`
    margin-right: 10px;
    display: flex;
`

const TourButtonPlaceholder = styled.div`
    width: 25px;
    height: 25px;
    visibility: hidden;
`

interface ChooseDocumentStepHeaderProps {
    workPath: WorkPath;
}

export const ChooseDocumentStepHeader = (props: ChooseDocumentStepHeaderProps) => {
    return (
        <Container>
            <WorkPathHeaderEditableName workPath={props.workPath} />
            <WorkPathHeaderDocSetName clusterId={props.workPath.clusterId} />
        </Container >
    )
}

interface ReportHeaderProps {
    workPath: WorkPath;
    documentId: string;
    onPublish: () => void;
}

export const ReportHeader = (props: ReportHeaderProps) => {
    const isPublished = useAppSelector(selectIsReportPublished);
    const viewMode = useAppSelector(selectReportViewMode);
    const context = useAppSelector(selectReportContext);

    const workPathType = useAppSelector(state => state.workPaths.currentWorkPath?.workPathType);
    const isPublishing = useAppSelector(selectIsAnyReportDocumentPublishing);

    const { availableViewModes, onViewModeChange } = useReportViewModes(props.documentId, workPathType);

    return (
        <Container data-automation-id={getAutomationIdForReportPageAnchor(workPathType, isPublished)}>
            <WorkPathHeaderEditableName workPath={props.workPath} />
            <WorkPathHeaderDocSetName clusterId={props.workPath.clusterId} />
            <HeaderInfoSection label={'status'}>
                {isPublishing ? 'Publishing' : !!isPublished ? 'Published' : 'Designing'}
            </HeaderInfoSection>
            <ActionsContainer data-automation-id={AutomationId.WorkPathActionCommands}>
                <TourButtonPlaceholder
                    data-automation-id={getAutomationIdForInfoButton(viewMode)}
                />
                {context === ReportViewContext.Review && <ReportReviewButton onPublish={props.onPublish} />}
                <HeaderInfoSectionCentered label={'view'}>
                    <StyledReportViewModeSwitcher
                        viewDescriptions={availableViewModes}
                        currentMode={viewMode}
                        onViewChange={onViewModeChange} />
                </HeaderInfoSectionCentered>
                <StyledWorkPathCommandBar />
            </ActionsContainer>
        </Container >
    )
}
